import { TIContainerInformationExtendedValid, sortRowsArray } from "@tedivo/tedivo-bay-grid-pure";

import { feetToMillimeters } from "@tedivo/tedivo-pure-helpers";

export type TSideViewSide = "port" | "starboard";

export interface ICullOccludedContainersOptions {
  /**
   * Height, in mm, of every container's rectangle, instead of its own `height`.
   * Pass the height that is actually drawn (for example the uniform height of the side view).
   */
  heightInMm?: number;
  /** Gaps and slivers up to this size (mm) are ignored. Default 60 mm (about 1 % of a 20') */
  tolerance?: number;
}

const DEFAULT_TOLERANCE_MM = 60;

type TRect = { x1: number; x2: number; y1: number; y2: number };

/**
 * Removes the containers of a bay-level that cannot be seen from one side of the ship.
 *
 * Every container is a rectangle in the side view: `lcg ± size / 2` along the ship and
 * `bottomBase .. bottomBase + height` vertically. Walking the rows from the viewer inwards, a
 * container is dropped when the containers nearer to the viewer already cover its whole rectangle.
 * A container that is only partly covered is kept: the result is returned **from the farthest row
 * to the nearest**, so drawing it in that order (painter's order) hides the covered part and shows
 * the rest, for example the AFT half of a 40 behind a FWD 20.
 *
 * Pure: it only reads `cgs` and `size` / `height`, no DOM.
 */
export function cullOccludedContainers(
  containers: TIContainerInformationExtendedValid[],
  side: TSideViewSide,
  options: ICullOccludedContainersOptions = {},
): TIContainerInformationExtendedValid[] {
  const { heightInMm, tolerance = DEFAULT_TOLERANCE_MM } = options;

  // Nearest to the viewer first. sortRowsArray orders port outer → 00 → stbd outer.
  const nearestFirst = containers
    .slice()
    .sort((a, b) => (side === "port" ? sortRowsArray(a.row, b.row) : sortRowsArray(b.row, a.row)));

  const kept: Array<{ container: TIContainerInformationExtendedValid; rect: TRect | undefined }> =
    [];

  for (const container of nearestFirst) {
    const rect = toRect(container, heightInMm);

    if (rect) {
      const nearerRects = kept
        .map((k) => k.rect)
        .filter((r): r is TRect => r !== undefined && rectsOverlap(r, rect, tolerance));

      if (nearerRects.length > 0 && isCovered(rect, nearerRects, tolerance)) continue;
    }

    kept.push({ container, rect });
  }

  return kept.map((k) => k.container).reverse();
}

/** Port when the criteria looks at the ship from port, starboard otherwise */
export function getSideOfRow(row: string): TSideViewSide {
  const n = Number(row);
  return Number.isFinite(n) && n !== 0 && n % 2 === 0 ? "port" : "starboard";
}

function toRect(
  container: TIContainerInformationExtendedValid,
  heightInMm: number | undefined,
): TRect | undefined {
  const { cgs, size, height } = container;
  const lcg = cgs?.lcg;

  if (lcg === undefined) return undefined;

  const halfLength = feetToMillimeters(size) * 0.5;
  const y1 = cgs.bottomBaseWithSeparation ?? cgs.bottomBase ?? 0;
  const h = heightInMm ?? height;

  return { x1: lcg - halfLength, x2: lcg + halfLength, y1, y2: y1 + h };
}

function rectsOverlap(a: TRect, b: TRect, tolerance: number): boolean {
  return (
    a.x1 < b.x2 - tolerance &&
    a.x2 > b.x1 + tolerance &&
    a.y1 < b.y2 - tolerance &&
    a.y2 > b.y1 + tolerance
  );
}

/** True when the union of `covers` contains `rect`, ignoring gaps smaller than `tolerance` */
function isCovered(rect: TRect, covers: TRect[], tolerance: number): boolean {
  // Vertical strips between every x edge that falls inside the rect
  const xs = Array.from(
    new Set(
      [rect.x1, rect.x2, ...covers.flatMap((c) => [c.x1, c.x2])]
        .filter((x) => x >= rect.x1 && x <= rect.x2)
        .map((x) => Math.round(x)),
    ),
  ).sort((a, b) => a - b);

  for (let i = 0; i < xs.length - 1; i++) {
    const xa = xs[i];
    const xb = xs[i + 1];

    if (xb - xa <= tolerance) continue;

    // The rects spanning this strip must cover the whole height of the rect
    const yIntervals = covers
      .filter((c) => c.x1 <= xa + tolerance && c.x2 >= xb - tolerance)
      .map((c) => ({ y1: c.y1, y2: c.y2 }))
      .sort((a, b) => a.y1 - b.y1);

    let reached = rect.y1;

    for (const iv of yIntervals) {
      if (iv.y1 > reached + tolerance) break;
      if (iv.y2 > reached) reached = iv.y2;
    }

    if (reached < rect.y2 - tolerance) return false;
  }

  return true;
}
